import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CalendarCheck, UserPlus, LogIn, Gift, ShoppingBag, Home, MapPin, Phone, Star } from "lucide-react";
import { api } from "../../lib/api";
import { useT } from "../../lib/i18n";
import PublicFrame, { usePublicBranding } from "./PublicFrame";

const TXT = {
  en: { welcome: "Welcome", sub: "Book, manage your appointments and treat yourself, all in one place.", book: "Book an appointment", signup: "Create an account",
    login: "Patient login", home: "Book a home visit", gifts: "Packages & gift cards", shop: "Shop", reviews: "Reviews", where: "Our locations", none: "Clinic not found." },
  it: { welcome: "Benvenuti", sub: "Prenota, gestisci i tuoi appuntamenti e concediti un regalo, tutto in un unico posto.", book: "Prenota un appuntamento", signup: "Crea un account",
    login: "Accesso pazienti", home: "Prenota a domicilio", gifts: "Pacchetti e buoni regalo", shop: "Shop", reviews: "Recensioni", where: "Le nostre sedi", none: "Clinica non trovata." },
};

function Action({ to, icon: Icon, label, primary, testId }) {
  return (
    <Link to={to} data-testid={testId}
      className={`group flex items-center gap-3 rounded-[var(--radius)] border p-4 font-medium transition-[transform,background-color,border-color] hover:-translate-y-0.5 ${primary ? "bg-primary text-primary-foreground border-primary sm:col-span-2 py-5 text-lg" : "bg-card hover:border-primary"}`}>
      <Icon className={`h-5 w-5 shrink-0 ${primary ? "" : "text-primary"}`} />{label}
    </Link>
  );
}

export default function ClinicHome() {
  const { slug } = useParams();
  const { lang } = useT();
  const l = TXT[lang] || TXT.en;
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/public/c/${slug}/hub`).then((r) => setData(r.data)).catch(() => setData(false)); }, [slug]);
  usePublicBranding(data);
  if (data === false) return <PublicFrame><p data-testid="clinic-home-not-found">{l.none}</p></PublicFrame>;
  if (!data) return <PublicFrame><p className="text-muted-foreground">…</p></PublicFrame>;
  const name = data.branding?.clinic_name || data.name;
  const f = data.features || {};
  return (
    <PublicFrame branding={data.branding}>
      <div className="space-y-10" data-testid="clinic-home">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold">{l.welcome}</p>
          <h1 className="text-4xl sm:text-5xl font-bold mt-2" data-testid="clinic-home-name">{name}</h1>
          <p className="text-muted-foreground mt-3 max-w-lg">{l.sub}</p>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <Action primary to={`/book/${slug}`} icon={CalendarCheck} label={l.book} testId="clinic-home-book" />
          <Action to={`/p/${slug}?mode=register`} icon={UserPlus} label={l.signup} testId="clinic-home-signup" />
          <Action to={`/p/${slug}`} icon={LogIn} label={l.login} testId="clinic-home-login" />
          {f.home && <Action to={`/home/${slug}`} icon={Home} label={l.home} testId="clinic-home-homevisit" />}
          {f.packages !== false && <Action to={`/packages/${slug}`} icon={Gift} label={l.gifts} testId="clinic-home-packages" />}
          {f.shop && <Action to={`/shop/${slug}`} icon={ShoppingBag} label={l.shop} testId="clinic-home-shop" />}
          {f.reviews !== false && <Action to={`/reviews/${slug}`} icon={Star} label={l.reviews} testId="clinic-home-reviews" />}
        </div>
        {data.locations?.length > 0 && (
          <div className="space-y-3">
            <h2 className="text-lg font-semibold">{l.where}</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              {data.locations.map((x) => (
                <div key={x.id} className="rounded-[var(--radius)] border bg-card p-4" data-testid={`clinic-home-location-${x.id}`}>
                  <div className="flex items-center gap-2 font-medium"><MapPin className="h-4 w-4 text-primary" />{x.name}</div>
                  {x.address && <p className="text-sm text-muted-foreground mt-1">{x.address}</p>}
                  {x.phone && <a href={`tel:${x.phone}`} className="text-sm text-primary mt-1 inline-flex items-center gap-1"><Phone className="h-3.5 w-3.5" />{x.phone}</a>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </PublicFrame>
  );
}
